/**
 * `personalize` (§7.4) — per-(user, gate) alias/icon/color. Ported from
 * `prototype/screens/personalize.js`. The alias is free text; presets only fill the field.
 * `alias: null` on save is the "Reset to default" path (unused by this screen's own UI, which
 * always sends a concrete string — reset lives nowhere else in the MVP, matching the prototype).
 *
 * **D247 — Save moved to the app bar and leaving now asks.** It is the same button doing the same
 * thing (`patchView` → device cache → `resetTo` Home, which is `flows.md`'s completed-flow rule and
 * predates this change); what changed is where it sits and *when it exists*. It renders **only
 * while the screen is dirty**, so an untouched visit has no control to press and no question on the
 * way out, and a back press with edits outstanding opens `UnsavedChangesPopover` rather than
 * discarding them in silence.
 */
import { useCallback, useEffect, useRef, useState } from 'react'
import type { GateId } from '@selogate/core'
import { useDeviceCache } from '../../device-cache/context'
import { useSession } from '../../auth/context'
import {
  AppBar,
  Icon,
  type IconName,
  DEFAULT_VIEW_COLOR,
  personalizationColor,
  renderNameForViewIcon,
  isViewColor,
  VIEW_COLORS,
  VIEW_ICONS,
  UnsavedChangesPopover,
  type ViewColorValue,
  type ViewIconValue,
} from '../../components'
import { useTranslation } from '../../i18n/I18nProvider'
import { useLeaveGuard, useNavigation } from '../../router'
import { getGate, patchView } from '../shared/api'

/** What the server holds — which is what `dirty` is measured against, never what was rendered. */
interface ViewBaseline {
  readonly alias: string
  readonly icon: ViewIconValue
  readonly color: ViewColorValue
}

export function PersonalizeScreen({ gateId }: { readonly gateId: GateId }): JSX.Element {
  const session = useSession()
  const deviceCache = useDeviceCache()
  const navigate = useNavigation()
  const { t } = useTranslation('personalize')
  const { t: tCommon } = useTranslation('common')

  const [defaultName, setDefaultName] = useState<string | undefined>(undefined)
  const [alias, setAlias] = useState('')
  const [icon, setIcon] = useState<ViewIconValue>('house')
  const [color, setColor] = useState<ViewColorValue>(DEFAULT_VIEW_COLOR)
  const [baseline, setBaseline] = useState<ViewBaseline | undefined>(undefined)
  const [saving, setSaving] = useState(false)
  const [saveError, setSaveError] = useState(false)

  /**
   * Set by the first edit to any of the three fields.
   *
   * The `getGate` response below lands *after* the screen is interactive, and it used to write
   * `icon`/`color` **unconditionally** — so a swatch tapped inside that window was reverted under
   * the finger. Only `alias` was protected, by the functional update it still uses. D247 raises the
   * stakes: the same response now also seeds the baseline, so without this the screen could adopt a
   * value the resident had just replaced **and** record it as the value they started from, which
   * renders as clean and hides the edit from the guard.
   */
  const touched = useRef(false)

  useEffect(() => {
    const cached = deviceCache.gates.find((g) => g.gateId === gateId)
    if (cached !== undefined) {
      const cachedAlias = cached.alias ?? cached.defaultName
      const cachedIcon = (cached.icon ?? 'house') as ViewIconValue
      const cachedColor =
        cached.color !== undefined && isViewColor(cached.color) ? cached.color : DEFAULT_VIEW_COLOR
      setDefaultName(cached.defaultName)
      setAlias(cachedAlias)
      setIcon(cachedIcon)
      setColor(cachedColor)
      setBaseline({ alias: cachedAlias, icon: cachedIcon, color: cachedColor })
    }
    void getGate(session.request, gateId).then((result) => {
      if (!result.ok) return
      const serverIcon = (result.data.icon ?? 'house') as ViewIconValue
      const serverColor =
        result.data.color !== undefined && isViewColor(result.data.color)
          ? result.data.color
          : DEFAULT_VIEW_COLOR
      const serverAlias = result.data.alias ?? result.data.defaultName
      setDefaultName(result.data.defaultName)
      // The baseline always follows the server: it is a statement about what is stored, not about
      // what is on screen. The fields themselves yield to an edit already in progress.
      setBaseline({ alias: serverAlias, icon: serverIcon, color: serverColor })
      setAlias((current) =>
        touched.current
          ? current
          : (result.data.alias ?? (current.length > 0 ? current : result.data.defaultName)),
      )
      if (!touched.current) {
        setIcon(serverIcon)
        setColor(serverColor)
      }
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [gateId])

  const dirty =
    baseline !== undefined && (alias !== baseline.alias || icon !== baseline.icon || color !== baseline.color)

  // Registered above the loading early-return: hooks may not sit behind a conditional.
  const guard = useLeaveGuard(
    'personalize:leave',
    dirty,
    useCallback(() => navigate.back(), [navigate]),
  )

  if (defaultName === undefined) {
    return (
      <>
        <AppBar title={t('title', { name: '' })} onBack={guard.requestLeave} />
        <div style={{ padding: 40, textAlign: 'center', color: 'var(--muted)' }}>{tCommon('loading')}</div>
      </>
    )
  }

  async function save(): Promise<void> {
    setSaving(true)
    setSaveError(false)
    const result = await patchView(session.request, gateId, { alias, icon, color })
    setSaving(false)
    if (!result.ok) {
      setSaveError(true)
      return
    }
    await deviceCache.updateGateView(gateId, { alias, icon, color })
    // `resetTo`, not `back` — a completed personalization lands on Home (`flows.md`), and it goes
    // through the router's `commit()`, which unwinds the guard's own history entry on the way out.
    navigate.resetTo({ id: 'home' })
  }

  /** Every field edit routes through here so `touched` cannot be forgotten on a new control. */
  function edit(apply: () => void): void {
    touched.current = true
    apply()
  }

  return (
    <>
      <AppBar
        title={t('title', { name: defaultName })}
        onBack={guard.requestLeave}
        action={
          dirty
            ? {
                label: saving ? tCommon('saving') : tCommon('save'),
                onClick: () => void save(),
                disabled: saving,
              }
            : undefined
        }
      />
      <div className="form-row">
        <input
          id="gateAlias"
          className="input"
          value={alias}
          maxLength={40}
          autoComplete="off"
          spellCheck={false}
          aria-label={t('aliasFieldLabel')}
          onChange={(e) => edit(() => setAlias(e.target.value))}
        />
      </div>
      <div className="icon-pick-row" role="group" aria-label={t('iconSectionTitle')}>
        {VIEW_ICONS.map((name) => (
          <button
            key={name}
            className={`icon-pick${name === icon ? ' sel' : ''}`}
            type="button"
            aria-pressed={name === icon}
            aria-label={t(`iconName_${name}` as Parameters<typeof t>[0])}
            style={name === icon ? { color: personalizationColor(color) } : undefined}
            onClick={() => edit(() => setIcon(name))}
          >
            <Icon name={renderNameForViewIcon(name) as IconName} size={48} />
          </button>
        ))}
      </div>
      <div className="color-pick-row" role="group" aria-label={t('colorSectionTitle')}>
        {VIEW_COLORS.map((name) => (
          <button
            key={name}
            className={`color-sw${name === color ? ' sel' : ''}`}
            type="button"
            aria-pressed={name === color}
            aria-label={t(`colorName_${name}` as Parameters<typeof t>[0])}
            onClick={() => edit(() => setColor(name))}
          >
            <span className="color-dot" style={{ background: personalizationColor(name) }} />
          </button>
        ))}
      </div>
      {saveError ? <p className="hint error">{t('saveFailed')}</p> : null}
      {guard.prompting ? (
        <UnsavedChangesPopover
          title={tCommon('unsavedTitle')}
          body={tCommon('unsavedBody')}
          saveLabel={tCommon('save')}
          discardLabel={tCommon('discardChanges')}
          stayLabel={tCommon('stayHere')}
          saveDisabled={saving}
          onSave={() => void save()}
          onDiscard={guard.leave}
          onStay={guard.stay}
        />
      ) : null}
    </>
  )
}
